<template>
	<div>
		<!-- Simple case -->
		<template v-if="scalarValueCase === 'simple'">
			<component
				:is="newValue.componentName"
				class="xs-12"
				v-model="newValue[newValue.scalarValueType]"
				@input="onInput"
				:size="size"
			/>
		</template>

		<!-- Complex case -->
		<template v-else>
			<div v-for="(item, i) in newValue.objectValue" :key="i" class="mb4">
				<div class="fw7 c6 lh125">{{ item.label | unCamelCase }}</div>
				<component
					:is="item.componentName"
					class="xs-12"
					v-model="item.value[item.scalarValueType]"
					@input="onInput"
					:size="size"
				/>
			</div>
		</template>
	</div>
</template>

<script>
import JBaseInputText from "@/components/JBaseInputText"
import JBaseInputNumber from "@/components/JBaseInputNumber"
import JBaseSelect from "@/components/JBaseSelect"
import JBaseToggle from "@/components/JBaseToggle"
import JBaseDatePickerSimple from "@/components/JBaseDatePickerSimple"

/**
 * This component will display proper form input based on scalarValue shape
 * There is numerous cases we have to support right now - simple once like stringValue or numberValue and comprehensive ones with objectValue
 */
export default {
	components: {
		JBaseInputText,
		JBaseInputNumber,
		JBaseSelect,
		JBaseToggle,
		JBaseDatePickerSimple
	},
	props: {
		/**
		 * Expecting subset of scalarValue from GraphQl
		 * If you pass stringValue and also numberValue at the same time, it won't work. You have to pass already sanitised value
		 */
		value: {
			type: Object,
			default: () => ({
				numberValue: 0
			})
		},
		size: {
			type: String,
			default: "xsmall"
		}
	},

	data() {
		return {
			newValue: {}
		}
	},

	computed: {
		scalarValueCase() {
			if (!this.value) return "simple"
			if (this.value.objectValue !== undefined) return "complex"
			return "simple"
		}
	},
	methods: {
		/**
		 * @description Here we parse this.value and prepare newValue that has more information for UI what to render
		 */
		initNewValue() {
			const copyOfValue = JSON.parse(JSON.stringify(this.value)) // create immutable copy

			/**
			 * Helper function that will decide data type of the component
			 */
			const decideScalarValueType = value => {
				if (!value) return "numberValue"
				if (value.numberValue !== undefined) return "numberValue"
				if (value.booleanValue !== undefined) return "booleanValue"
				if (value.stringValue !== undefined) return "stringValue"
				if (value.dateValue !== undefined) return "dateValue"
				if (value.objectValue !== undefined) return "objectValue"
				return "numberValue"
			}

			/**
			 * Helper function that will decide what component to display based on if it's numberValue, stringValue, booleanValue or dateValue
			 */
			const decideInput = scalarValueType => {
				if (scalarValueType === "numberValue") return "j-base-input-number"
				if (scalarValueType === "booleanValue") return "j-base-toggle"
				if (scalarValueType === "stringValue") return "j-base-input-text"
				if (scalarValueType === "dateValue") return "j-base-date-picker-simple"
				return "j-base-input-number"
			}

			// Simple case - displaying only one input
			if (this.scalarValueCase === "simple") {
				const scalarValueType = decideScalarValueType(copyOfValue)
				copyOfValue.componentName = decideInput(scalarValueType)
				copyOfValue.scalarValueType = scalarValueType // define what scalarValueType it is
				// copyOfValue[scalarValueType] = copyOfValue[scalarValueType] // here we save value from the input
			}

			// Complex case - displaying more inputs based on objectValue
			if (this.scalarValueCase === "complex") {
				copyOfValue.objectValue.forEach(item => {
					item.label = item.key
					item.componentName = decideInput(decideScalarValueType(item.value))
					item.scalarValueType = decideScalarValueType(item.value) // define what scalarValueType it is
				})
			}

			this.newValue = copyOfValue
		},

		onInput() {
			// Sanitize this.newValue so that we return clean ScalarValue for graphQL api mutation
			// It means we will get rid of some additional properties we added on the object like componentName etc.
			if (this.scalarValueCase === "simple") {
				this.$emit("input", {
					[this.newValue.scalarValueType]: this.newValue[
						this.newValue.scalarValueType
					]
				})
			} else {
				const output = this.newValue.objectValue.map(item => {
					return {
						key: item.key,
						value: item.value
					}
				})
				this.$emit("input", { objectValue: output })
			}
		}
	},

	watch: {
		/**
		 * Every time value updates, parse scalarValues and prepare newValue for the form
		 */
		value: {
			handler() {
				this.initNewValue()
			},
			immediate: true,
			deep: true
		}
	},

	filters: {
		unCamelCase(value) {
			if (!value) return ""
			return value
				.replace(/([A-Z])/g, " $1") // insert a space before all caps
				.toLowerCase()
		}
	}
}
</script>